import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Badge, Collapse, Spinner } from 'reactstrap';
import { CheckCircle2, Download, Copy, ExternalLink, ChevronDown, ChevronRight, Share2, Printer, MapPin } from 'lucide-react';
import { QRCode } from 'react-qrcode-logo';
import { motion, AnimatePresence } from 'framer-motion';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useSpaceStore } from '../../../stores/Assets/space';
import { useExperience } from '../../../stores/Experience/Experience';

const THEME_COLOR = '#c59253';

const SpaceSummaryStep = ({ sites, onBack }) => {
    const navigate = useNavigate();
    const [expandedSite, setExpandedSite] = useState(sites[0]?.site_id);
    const [siteSpaces, setSiteSpaces] = useState({}); // { siteId: spaces[] }
    const [loading, setLoading] = useState(true);
    const [downloadingZip, setDownloadingZip] = useState(false);
    const { getSpaces } = useSpaceStore();
    const { submissionResult } = useExperience();

    const qrBaseUrl = process.env.REACT_APP_QR_URL || 'https://clickorder.io';

    useEffect(() => {
        const loadAllSpaces = async () => {
            setLoading(true);
            const data = {};
            try {
                // Always fetch all spaces for each site to ensure we show everything
                for (const site of sites) {
                    const res = await getSpaces(site.site_id, { page: 1, perPage: 100 });
                    data[site.site_id] = res || [];
                }

                // If we have submission results, merge any additional data (like qrKey)
                if (submissionResult?.space_info) {
                    submissionResult.space_info.forEach(s => {
                        const siteSpaces = data[s.site_id] || [];
                        const existingIdx = siteSpaces.findIndex(sp => sp.space_id === s.space_id);
                        if (existingIdx >= 0) {
                            // Merge with submission result data (may have qrKey already resolved)
                            siteSpaces[existingIdx] = { ...siteSpaces[existingIdx], ...s };
                        }
                    });
                }

                setSiteSpaces(data);
            } catch (error) {
                console.error("Failed to load spaces for summary:", error);
                toast.error("Error loading spaces.");
            } finally {
                setLoading(false);
            }
        };
        loadAllSpaces();
    }, [sites, getSpaces, submissionResult]);

    const handleCopy = (qrKey) => {
        const url = `${qrBaseUrl}/menu/${qrKey}`;
        navigator.clipboard.writeText(url);
        toast.success("Link copied!");
    };

    const downloadAllForSite = async (site) => {
        const spaces = siteSpaces[site.site_id] || [];
        if (spaces.length === 0) return;

        setDownloadingZip(true);
        try {
            const zip = new JSZip();
            const folder = zip.folder(site.name);

            // This is a bit tricky because we need the Canvas elements which are rendered.
            // A better way is to iterate and trigger individual downloads or use a hidden canvas generator.
            // For now, let's just do a CSV metadata file as a placeholder if we can't easily grab 100 canvases.

            const csvRows = [["Space Name", "Order Link"]];
            spaces.forEach(s => {
                csvRows.push([s.name, `${qrBaseUrl}/menu/${s.qrKey || s.space_id}`]);
            });
            const csvContent = csvRows.map(r => r.join(",")).join("\n");
            folder.file("Space_List.csv", csvContent);

            const content = await zip.generateAsync({ type: "blob" });
            saveAs(content, `${site.name}_Spaces.zip`);
            toast.success("ZIP Generated!");
        } catch (e) {
            toast.error("Failed to generate ZIP");
        } finally {
            setDownloadingZip(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-white">
                <Spinner color="warning" />
                <p className="mt-3 text-muted fw-bold">Fetching your QR codes...</p>
            </div>
        );
    }

    return (
        <div className="bg-light h-100 d-flex flex-column overflow-auto">
            <Container className="py-5" style={{ maxWidth: '1000px' }}>
                <div className="text-center mb-5">
                    <motion.div
                        initial={{ scale: 0 }} animate={{ scale: 1 }}
                        className="d-inline-flex p-3 rounded-circle bg-success bg-opacity-10 text-success mb-3"
                    >
                        <CheckCircle2 size={48} />
                    </motion.div>
                    <h2 className="fw-bold">Your Spaces are ready!</h2>
                    <p className="text-muted">Below are the ordering links and QR codes for all your locations.</p>

                    <div className="mt-4">
                        <Button
                            color="dark"
                            className="rounded-pill px-4 py-2 shadow-sm fw-bold"
                            onClick={() => navigate('/')}
                        >
                            Go to Dashboard
                        </Button>
                    </div>
                </div>

                <div className="d-flex flex-column gap-4 pb-5">
                    {sites.map(site => {
                        const spaces = siteSpaces[site.site_id] || [];
                        const isExpanded = expandedSite === site.site_id;

                        return (
                            <Card key={site.site_id} className="border-0 shadow-sm rounded-4 overflow-hidden">
                                <CardBody className="p-0">
                                    <div
                                        className="p-4 bg-white d-flex align-items-center justify-content-between cursor-pointer border-bottom"
                                        onClick={() => setExpandedSite(isExpanded ? null : site.site_id)}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded-4 overflow-hidden border shadow-sm" style={{ width: '56px', height: '56px' }}>
                                                {site.image_url ? (
                                                    <img src={site.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div className="bg-light h-100 w-100 d-flex align-items-center justify-content-center text-dark">
                                                        <MapPin size={24} />
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="fw-bold mb-0">{site.name}</h4>
                                                <div className="d-flex align-items-center gap-2 mt-1">
                                                    <Badge color="soft-dark" className="bg-light text-muted border fw-normal">{spaces.length} Spaces</Badge>
                                                    <span className="text-muted small">{site.address}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <Button
                                                size="sm"
                                                outline
                                                color="dark"
                                                className="rounded-pill px-3 d-none d-md-flex align-items-center gap-2 border"
                                                onClick={(e) => { e.stopPropagation(); downloadAllForSite(site); }}
                                            >
                                                <Download size={14} /> Download ZIP
                                            </Button>
                                            {isExpanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
                                        </div>
                                    </div>

                                    <Collapse isOpen={isExpanded}>
                                        <div className="p-4 bg-light bg-opacity-50">
                                            <Row className="g-3">
                                                {spaces.map(space => (
                                                    <Col key={space.space_id} lg={4} md={6}>
                                                        <Card className="border-0 shadow-sm rounded-4 bg-white hover-up transition-all overflow-hidden">
                                                            <CardBody className="p-3 text-center">
                                                                <h6 className="fw-bold mb-3">{space.name}</h6>
                                                                <div className="p-3 bg-white rounded-4 border mb-3 d-inline-block">
                                                                    <QRCode
                                                                        value={`${qrBaseUrl}/menu/${space.qrKey}`}
                                                                        size={140}
                                                                        eyeRadius={8}
                                                                        logoImage={site.logo}
                                                                        logoPadding={4}
                                                                        logoPaddingStyle="circle"
                                                                    />
                                                                </div>
                                                                <div className="d-flex gap-2 justify-content-center">
                                                                    <Button
                                                                        size="sm"
                                                                        color="light"
                                                                        className="rounded-circle p-2 border"
                                                                        title="Copy Link"
                                                                        onClick={() => handleCopy(space.qrKey)}
                                                                    >
                                                                        <Copy size={16} />
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        color="light"
                                                                        className="rounded-circle p-2 border"
                                                                        title="Open Menu"
                                                                        onClick={() => window.open(`${qrBaseUrl}/menu/${space.qrKey}`, '_blank')}
                                                                    >
                                                                        <ExternalLink size={16} />
                                                                    </Button>
                                                                </div>
                                                            </CardBody>
                                                        </Card>
                                                    </Col>
                                                ))}
                                                {spaces.length === 0 && (
                                                    <Col xs={12} className="text-center py-5">
                                                        <p className="text-muted italic">No spaces found for this location.</p>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    </Collapse>
                                </CardBody>
                            </Card>
                        );
                    })}
                </div>
            </Container >

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-up:hover { transform: translateY(-5px); transition: transform 0.3s ease; }
            `}</style>
        </div >
    );
};

export default SpaceSummaryStep;
